import { useRef, useEffect, useState, Dispatch, SetStateAction } from "react";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  SimpleGrid,
  Stack,
  useToast,
  VStack,
  HStack,
  GridItem,
  FormControl,
  Input,
  Spinner,
} from "@chakra-ui/react";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { warnValidation } from "../../../../../services/utils";
import InputField from "../../../../../components/InputField";
import TextareaField from "../../../../../components/TextareaField";
import { AddressFormData, CompanyFormData, ContactFormData } from "../../types";
import {
  getCompany,
  saveCompany,
  updateCompany,
} from "../../../../../services/companyService";
import { estados } from "../../../../../models/Estados";
import MultipleSelect from "../../../../../components/MultipleSelect";
import SelectField from "../../../../../components/SelectField";
import { DateTime } from "luxon";
import { getCnpjData } from "../../../../../services/cnpjService";
import { crm } from "../../../../../theme";
import {
  COMPANY_OPERATIONS,
  COMPANY_TYPE_OPTIONS,
} from "../../../../../models/Company";

interface CompanyFormProps {
  id?: string;
  set: Dispatch<SetStateAction<any>>;
  get: () => void;
  setClientId?: (id: string) => void;
}

const CompanyForm = ({ id, set, get, setClientId }: CompanyFormProps) => {
  const [initialData, setInitialData] = useState<CompanyFormData>();
  const [segments, setSegments] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const formRef = useRef<FormHandles>(null);
  const [cnpjSearch, setCnpjSearch] = useState("");
  const [cnpjLoading, setCnpjLoading] = useState(false);

  const toast = useToast();
  const isEdit = !!id;

  const [addresses, setAddresses] = useState<AddressFormData[]>(
    initialData?.addresses || []
  );
  const [contacts, setContacts] = useState<ContactFormData[]>(
    initialData?.contacts || []
  );

  async function handleSubmit(data: CompanyFormData) {
    try {
      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        name: Yup.string().required("Campo obrigatório"),
        cnpj: Yup.string().required("Campo obrigatório"),
        status: Yup.string().required("Campo obrigatório"),
        // Unidade define quem vê o cliente (Emular cliente/listas) antes do primeiro processo
        companyType: Yup.string().required("Campo obrigatório"),
        addresses: Yup.array().of(
          Yup.object().shape({
            street: Yup.string().required("Campo obrigatório"),
            number: Yup.string().required("Campo obrigatório"),
            city: Yup.string().required("Campo obrigatório"),
            state: Yup.string().required("Campo obrigatório"),
            zipCode: Yup.string().required("Campo obrigatório"),
          })
        ),
        contacts: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required("Nome é obrigatório"),
          })
        ),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      // Filter out empty addresses and contacts
      data.segment = segments.join(";");
      data.states = selectedStates.join(";");
      data.addresses =
        data.addresses
          ?.map((a) => ({
            ...a,
            id: (a.id?.length ?? 0) > 0 ? a.id : undefined,
          }))
          .filter(
            (addr) =>
              addr.street ||
              addr.city ||
              addr.state ||
              addr.zipCode ||
              addr.country
          ) ?? [];

      data.contacts =
        data.contacts
          ?.map((c) => ({
            ...c,
            id: (c.id?.length ?? 0) > 0 ? c.id : undefined,
          }))
          .filter(
            (contact) => contact.name || contact.email || contact.phoneNumber
          ) ?? [];

      // Selects do unform devolvem string; o backend grava SEMPRE o que vier (vazio = limpa).
      const rawCompanyType = data.companyType as unknown as string | number | null | undefined;
      data.companyType =
        rawCompanyType === undefined || rawCompanyType === null || rawCompanyType === ""
          ? null
          : Number(rawCompanyType);
      data.operation = data.operation ? data.operation : null;

      // Add id if editing
      if (isEdit) {
        data.id = id;
      }

      const response = isEdit
        ? await updateCompany(data)
        : await saveCompany(data);

      setClientId?.(response?.data.id);

      toast({
        title: isEdit ? "Cliente atualizado!" : "Cliente cadastrado!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      set(undefined);
      await get();
    } catch (err) {
      console.log("asd-err", err);
      warnValidation(err, formRef.current);
      toast({
        title: "Erro no formulário",
        description:
          "Ocorreu um erro ao processar o formulário, confira os dados.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  const addAddress = () => {
    setAddresses([
      ...addresses,
      {
        street: "",
        number: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
    ]);
  };

  const removeAddress = (index: number) => {
    const newAddresses = [...addresses];
    newAddresses.splice(index, 1);
    setAddresses(newAddresses);
  };

  const addContact = () => {
    setContacts([...contacts, { name: "", email: "", phoneNumber: "" }]);
  };

  const removeContact = (index: number) => {
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    setContacts(newContacts);
  };

  const getSavedCompany = async () => {
    const response = await getCompany(id ?? "");
    if (response) {
      setInitialData(response);
      setSegments(response.segment ? response.segment?.split(";") : []);
      setSelectedStates(response.states ? response.states?.split(";") : []);
      setContacts(response.contacts);
      setAddresses(response.addresses);
    }
  };

  useEffect(() => {
    if (id) {
      getSavedCompany();
    }
  }, [id]);

  const formatCnpj = (value: string) =>
    value
      .replace(/\D/g, "")
      .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, "$1.$2.$3/$4-$5");

  const formatCep = (value: string) =>
    value.replace(/\D/g, "").replace(/^(\d{5})(\d{3}).*/, "$1-$2");

  const fillFromCnpj = async () => {
    setCnpjLoading(true);
    const result = await getCnpjData(cnpjSearch);
    setCnpjLoading(false);

    if (result.error || !result.data) {
      toast({
        title: result.error ?? "Não foi possível consultar o CNPJ.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const d = result.data;
    const foundingDate = d.data_inicio_atividade
      ? DateTime.fromISO(d.data_inicio_atividade).toFormat("dd/MM/yyyy")
      : "";
    const address = {
      street: d.logradouro ?? "",
      number: d.numero ?? "",
      city: d.municipio ?? "",
      state: d.uf ?? "",
      zipCode: d.cep ? formatCep(d.cep) : "",
      country: "Brasil",
    };

    setAddresses([address]);
    if (d.uf) setSelectedStates([d.uf]);

    // Aguarda os campos de endereço serem renderizados antes de preencher.
    setTimeout(() => {
      formRef.current?.setData({
        name: d.razao_social ?? "",
        cnpj: d.cnpj ? formatCnpj(d.cnpj) : formatCnpj(cnpjSearch),
        tradeName: d.nome_fantasia ?? "",
        foundingDate,
        registrationStatus: d.descricao_situacao_cadastral ?? "",
        mainActivity: d.cnae_fiscal_descricao ?? "",
        status: "Lead",
        addresses: [address],
      });
    }, 80);

    toast({
      title: "Dados preenchidos a partir do CNPJ.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box>
      <Heading size="lg" mb="24px" fontWeight="normal">
        <Flex alignItems="center" gap="12px" mt="32px">
          {isEdit ? "Editar Cliente" : "Cadastrar Cliente"}
        </Flex>
      </Heading>
      {(isEdit ? initialData : !isEdit) && (
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialData}>
          <VStack spacing={6} align="stretch">
            {!isEdit && (
              <Box p={4} borderWidth="1px" borderRadius="12px" borderColor={crm.border} bg={crm.pageBg}>
                <FormLabel mb="8px" fontWeight="semibold">
                  Buscar dados da empresa pelo CNPJ
                </FormLabel>
                <HStack>
                  <Input
                    bg="white"
                    placeholder="Digite o CNPJ e clique em buscar"
                    value={cnpjSearch}
                    onChange={(e) => setCnpjSearch(e.target.value)}
                  />
                  <Button
                    backgroundColor={crm.brand}
                    color="white"
                    borderRadius="10px"
                    _hover={{ backgroundColor: crm.brandDark }}
                    minW="120px"
                    isLoading={cnpjLoading}
                    onClick={fillFromCnpj}
                  >
                    Buscar
                  </Button>
                </HStack>
              </Box>
            )}
            <Stack spacing={4}>
              <Heading size="md" fontWeight="semibold">
                Informações Básicas
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <InputField name="name" label="Nome da Empresa" />
                <InputField name="cnpj" label="CNPJ" mask="cnpj" />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <InputField name="tradeName" label="Nome Fantasia" />
                <InputField
                  name="foundingDate"
                  label="Data de Fundação"
                  mask="date"
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <InputField
                  name="registrationStatus"
                  label="Situação Cadastral"
                />
                <InputField
                  name="mainActivity"
                  label="Atividade Principal (CNAE)"
                />
              </SimpleGrid>
              <FormControl>
                <Box w="100%" mt={10}>
                  <FormLabel>Status</FormLabel>
                  <SelectField
                    name="status"
                    placeholder="Status"
                    options={[
                      { label: "Lead", value: "Lead" },
                      { label: "Ativo", value: "Ativo" },
                    ]}
                  />
                </Box>
              </FormControl>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <SelectField
                  name="companyType"
                  label="Empresa"
                  placeholder="Selecione a unidade"
                  options={COMPANY_TYPE_OPTIONS}
                />
                <SelectField
                  name="operation"
                  label="Operação"
                  options={COMPANY_OPERATIONS.map((operation) => ({
                    label: operation,
                    value: operation,
                  }))}
                />
              </SimpleGrid>
              <FormLabel>Estados</FormLabel>
              <MultipleSelect
                selectedValues={selectedStates}
                onChange={(values) => setSelectedStates(values)}
                placeholder="Selecione os estados"
                options={estados}
              />
              <FormLabel>Segmentos</FormLabel>
              <MultipleSelect
                selectedValues={segments}
                onChange={(values) => setSegments(values)}
                placeholder="Selecione os estados"
                options={["Pneus", "Autopeças", "Oficina", "Varejo", "Atacado"]}
              />
              <TextareaField name="observation" label="Observações" />
            </Stack>

            <Divider />

            <Stack spacing={4}>
              <Flex justify="space-between" align="center">
                <Heading size="md" fontWeight="semibold">
                  Endereços
                </Heading>
                <Button
                  leftIcon={<FiPlus />}
                  backgroundColor={crm.brand}
                  color="white"
                  borderRadius="10px"
                  _hover={{ backgroundColor: crm.brandDark }}
                  size="sm"
                  onClick={addAddress}
                >
                  Adicionar Endereço
                </Button>
              </Flex>

              {addresses.map((_, index) => (
                <Box
                  key={index}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  position="relative"
                >
                  <Flex justifyContent="flex-end">
                    <IconButton
                      aria-label="Remover endereço"
                      icon={<FiTrash2 />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => removeAddress(index)}
                    />
                  </Flex>

                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                      <GridItem colSpan={3}>
                        <Box display="none">
                          <InputField
                            name={`addresses[${index}].id`}
                            label="id"
                          />
                        </Box>
                        <InputField
                          name={`addresses[${index}].street`}
                          label="Rua"
                        />
                      </GridItem>
                      <InputField
                        name={`addresses[${index}].number`}
                        label="Número"
                      />
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <InputField
                        name={`addresses[${index}].city`}
                        label="Cidade"
                      />
                      <InputField
                        name={`addresses[${index}].state`}
                        label="Estado"
                      />
                      <InputField
                        name={`addresses[${index}].zipCode`}
                        label="CEP"
                        mask="cep"
                      />
                    </SimpleGrid>

                    <InputField
                      name={`addresses[${index}].country`}
                      label="País"
                    />
                  </VStack>
                </Box>
              ))}
            </Stack>

            <Divider />

            <Stack spacing={4}>
              <Flex justify="space-between" align="center">
                <Heading size="md" fontWeight="semibold">
                  Contatos
                </Heading>
                <Button
                  leftIcon={<FiPlus />}
                  backgroundColor={crm.brand}
                  color="white"
                  borderRadius="10px"
                  _hover={{ backgroundColor: crm.brandDark }}
                  size="sm"
                  onClick={addContact}
                >
                  Adicionar Contato
                </Button>
              </Flex>

              {contacts.map((contact, contactIndex) => (
                <Box
                  key={contactIndex}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  position="relative"
                >
                  <Flex justifyContent="flex-end">
                    <IconButton
                      aria-label="Remover contato"
                      icon={<FiTrash2 />}
                      size="sm"
                      colorScheme="red"
                      onClick={() => removeContact(contactIndex)}
                    />
                  </Flex>
                  <VStack spacing={4} align="stretch">
                    <Box display="none">
                      <InputField
                        name={`contacts[${contactIndex}].id`}
                        label="id"
                      />
                    </Box>
                    <InputField
                      name={`contacts[${contactIndex}].name`}
                      label="Nome"
                    />

                    <InputField
                      name={`contacts[${contactIndex}].position`}
                      label="Cargo"
                    />

                    <InputField
                      name={`contacts[${contactIndex}].email`}
                      label="Email"
                      type="email"
                    />
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <InputField
                        name={`contacts[${contactIndex}].phoneNumber`}
                        label="Telefone"
                        mask="phone"
                      />

                      <InputField
                        name={`contacts[${contactIndex}].cellphoneNumber`}
                        label="Celular"
                        mask="mobile"
                      />
                    </SimpleGrid>
                  </VStack>
                </Box>
              ))}
            </Stack>

            <Flex justify="flex-end" mt={6}>
              <HStack spacing={4}>
                <Button
                  variant="outline"
                  borderColor={crm.border}
                  color={crm.brand}
                  borderRadius="10px"
                  _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
                  onClick={() => set(undefined)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  backgroundColor={crm.brand}
                  color="white"
                  borderRadius="10px"
                  _hover={{ backgroundColor: crm.brandDark }}
                >
                  Salvar
                </Button>
              </HStack>
            </Flex>
          </VStack>
        </Form>
      )}
    </Box>
  );
};

export default CompanyForm;
